import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { api, buildUnauthorizedUrl } from './super-picker/services/api';

const SSO_REQUEST_DEDUP_KEY = 'sp_sso_request_inflight';
const SSO_REQUEST_DEDUP_TTL_MS = 30_000;
const NO_ACCESS_UPLOAD_TOKEN_KEY = 'sp_no_access_upload_token';

/** JWT is three base64url segments separated by dots. Extract from string in case token param has extra text (e.g. pasted command output). */
function extractJwtFromString(value: string): string {
  const trimmed = value.trim();
  const match = trimmed.match(/eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/);
  return match ? match[0] : trimmed;
}

/** Decode the `redirect` claim from a JWT payload without verifying the signature. */
function extractRedirectFromJwt(token: string): string | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padding = '='.repeat((4 - (base64.length % 4)) % 4);
    const payload = JSON.parse(atob(base64 + padding)) as Record<string, unknown>;
    return typeof payload.redirect === 'string' && payload.redirect.trim() ? payload.redirect.trim() : null;
  } catch {
    return null;
  }
}

function markSsoRequestInFlight(token: string): boolean {
  try {
    const now = Date.now();
    const currentRaw = sessionStorage.getItem(SSO_REQUEST_DEDUP_KEY);
    if (currentRaw) {
      const current = JSON.parse(currentRaw) as { token?: string; startedAt?: number };
      if (current.token === token && typeof current.startedAt === 'number' && now - current.startedAt < SSO_REQUEST_DEDUP_TTL_MS) {
        return false;
      }
    }
    sessionStorage.setItem(SSO_REQUEST_DEDUP_KEY, JSON.stringify({ token, startedAt: now }));
    return true;
  } catch {
    return true;
  }
}

/** Query params on htns.sg links to preserve through SSO (/sso → app route). */
// `id` carries the showcase for ?redirect=showcase&id=N.
const SSO_FORWARD_QUERY_PARAMS = ['redirect', 'projectId', 'ecoPropId', 'ecopropid', 'eventId', 'attendanceToken', 'id'] as const;

function buildPostSsoAppUrl(basePath: string, urlParams: URLSearchParams): string {
  const forward = new URLSearchParams();
  for (const key of SSO_FORWARD_QUERY_PARAMS) {
    const value = urlParams.get(key)?.trim();
    if (value) {
      forward.set(key, value);
    }
  }
  const query = forward.toString();
  return query ? `${basePath}?${query}` : basePath;
}

function clearSsoRequestInFlight(token: string): void {
  try {
    const currentRaw = sessionStorage.getItem(SSO_REQUEST_DEDUP_KEY);
    if (!currentRaw) return;
    const current = JSON.parse(currentRaw) as { token?: string };
    if (current.token === token) {
      sessionStorage.removeItem(SSO_REQUEST_DEDUP_KEY);
    }
  } catch {
    sessionStorage.removeItem(SSO_REQUEST_DEDUP_KEY);
  }
}

/**
 * Handles /sso callback: exchanges SSO token for session cookies and redirects to the target module.
 */
export function LoginApp() {
  const [status, setStatus] = useState<'loading' | 'error'>('loading');

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const rawToken = urlParams.get('token') || urlParams.get('ssoToken');
    const ssoToken = rawToken ? extractJwtFromString(rawToken) : '';
    // Prefer explicit ?app= URL param; fall back to the JWT's `redirect` claim so
    // HIP links that only carry a token still land on the right app.
    const requestedApp = urlParams.get('app') || extractRedirectFromJwt(ssoToken) || undefined;
    if (!ssoToken) {
      console.error('Missing SSO token on /sso route.');
      window.location.replace(buildUnauthorizedUrl('SSO_TOKEN_EXPIRED', requestedApp ? { app: requestedApp } : undefined));
      return;
    }

    if (!markSsoRequestInFlight(ssoToken)) {
      return;
    }

    api
      .ssoLogin(ssoToken, requestedApp)
      .then((data) => {
        clearSsoRequestInFlight(ssoToken);
        const appRedirect = typeof data.redirect === 'string' && data.redirect ? data.redirect.trim() : 'super-picker';
        const path = appRedirect.startsWith('/') ? appRedirect : `/${appRedirect}`;
        const safeRedirect = path.startsWith('/sso') ? '/super-picker' : path;
        // Forward deep-link params from htns.sg (e.g. ?redirect=my-transactions, ?projectId=xxxx)
        const finalUrl = buildPostSsoAppUrl(safeRedirect, urlParams);
        window.location.replace(finalUrl);
      })
      .catch((error) => {
        clearSsoRequestInFlight(ssoToken);
        console.error('SSO login failed:', error);
        setStatus('error');
        const errorCode = (error as { code?: string } | undefined)?.code;
        if (errorCode === 'NO_SUPER_PICKER_ACCESS' || errorCode === 'NO_SUPER_CALCULATOR_ACCESS') {
          try {
            sessionStorage.setItem(NO_ACCESS_UPLOAD_TOKEN_KEY, ssoToken);
          } catch {
            // Ignore storage failures
          }
        }
        window.location.replace(buildUnauthorizedUrl(errorCode, requestedApp ? { app: requestedApp } : undefined));
      });
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        gap: 2,
      }}
    >
      <CircularProgress />
      <Typography color="text.secondary">
        {status === 'loading' ? 'Signing you in...' : 'Redirecting...'}
      </Typography>
    </Box>
  );
}
